import React, { useState, useImperativeHandle, useRef } from "react";
import styled from "styled-components";
import { mdiCheck, mdiChevronDown } from "@mdi/js";

import { Svg } from "./Svg";
import { useClickOutside } from "../hooks/useClickOutside";

const SelectStyle = styled.div`
    position: relative;
`;

const ContainerStyle = styled.div`
    height: 26px;
    display: flex;
    border-radius: 2px;
    box-sizing: border-box;
    background-color: ${props => props.theme.white};
    border: 1px solid ${props => props.theme.greyLight};
    &:focus-within {
        border-color: ${props => props.theme.white};
        outline: 1px solid ${props => props.theme.primaryLight};
    }
    &.disabled {
        pointer-events: none;
        background-color: ${props => props.theme.greyLighter};
    }
    &.error {
        background-color: ${props => props.theme.redLight};
        border-color: ${props => props.theme.redDark};
        &:focus-within {
            border-color: ${props => props.theme.redLight};
            outline: 1px solid ${props => props.theme.redDarker};
        }
    }
`;

const TitleStyle = styled.p`
    height: 12px;
    font-size: 12px;
    margin-bottom: 1px;
    letter-spacing: 0.4px;
    color: ${props => props.theme.black};
`;

const InputStyle = styled.input`
    cursor: pointer;
    width: 100%;
    border: none;
    display: block;
    font-weight: 400;
    box-sizing: border-box;
    padding: 3px 5px 2px 10px;
    background-color: transparent;
    color: ${props => props.theme.black};
    text-align: ${props => props.$textAlign};
    &::placeholder {
        color: ${props => props.theme.greyLight};
    }
`;

const SymbolStyle = styled.div`
    cursor: pointer;
    height: 25px;
    display: flex;
    padding: 0 1px;
    align-items: center;
    box-sizing: border-box;
    border-left: 1px solid ${props => props.theme.greyLight};
    &.error {
        border-color: ${props => props.theme.redDark};
    }
`;

const OptionsStyle = styled.div`
    z-index: 10;
    display: block;
    font-size: 14px;
    position: absolute;
    margin-top: 5px;
    padding-top: 4px;
    padding-bottom: 4px;
    overflow-y: auto;
    max-height: 200px;
    overflow-x: visible;
    border-radius: 2px;
    background: ${props => props.theme.white};
    outline: 1px solid ${props => props.theme.primaryLight};
    &.error {
        outline: 1px solid ${props => props.theme.redDarker};
    }
`;

const OptionStyle = styled.div`
    cursor: pointer;
    user-select: none;
    display: flex;
    padding: 7px 10px 7px 10px;
    &:hover {
        background: ${props => props.theme.greyLightest};
    }
    >.checkbox {
        width: 10px;
        height: 10px;
        padding: 2px;
        font-size: 10px;
        border-radius: 2px;
        margin-right: 10px;
        border: 1px solid ${props => props.theme.greyLight};
        >svg {
            fill: ${props => props.theme.black};
        }
    }
`;

const ErrorStyle = styled.p`
    height: 0;
    opacity: 0;
    font-size: 11px;
    visibility: hidden;
    color: ${props => props.theme.redDark};
    transition: opacity 0.3s, height 0.3s, visibility 0.3s;
    &.error {
        opacity: 1;
        height: 11px;
        margin-top: 2px;
        visibility: visible;
    }
`;

const Option = ({ value, values, children, onChange }) => {

    const onClickOption = () => {
        const newValuesSet = new Set(values);
        if (newValuesSet.has(value)) {
            newValuesSet.delete(value);
        } else {
            newValuesSet.add(value);
        }
        onChange(Array.from(newValuesSet));
    };

    return (
        <OptionStyle
            value={value}
            onClick={onClickOption}
            onMouseDown={event => event.preventDefault()}>
            <div className="checkbox">
                {values.includes(value) &&
                    <Svg path={mdiCheck} viewBox="4 4 20 20" size="13" />
                }
            </div>
            {children}
        </OptionStyle>
    );
};

export const SelectMultiple = props => {

    const { id, ref, name, title, value, disabled, textAlign = "left", error, errorVisible, children } = props;

    const inputRef = useRef(null);
    const controlRef = useRef(null);
    const values = Array.isArray(value) ? value : [value];

    const [showOptions, setShowOptions] = useState(false);

    const errorName = name ? name + "ErrorText" : "errorText";

    const disabledClassName = disabled ? "disabled" : "";
    const errorClassName = (errorVisible && error) ? "error" : "";
    const className = [disabledClassName, errorClassName].join(" ");
    const optionsClassName = [errorClassName].join(" ");

    useImperativeHandle(ref, () => ({
        get value() {
            return value;
        },
        focus: () => inputRef.current.focus(),
    }), [inputRef, value]);

    useClickOutside(() => {
        setShowOptions(false);
    }, [controlRef]);

    const onClick = () => {
        inputRef.current.focus();
        setShowOptions(true);
    };

    const toLabel = () => {
        const labelFn = props.toLabel || (value => value);
        return values.map(labelFn).join(", ");
    };

    const onClickOption = newValues => {
        props.onChange(newValues);
        inputRef.current.focus();
        setShowOptions(true);
    };

    const buildOptions = childs => {
        const childrenArray = React.Children.toArray(childs);
        return childrenArray.map((child, index) => {
            const { value, children } = child.props;
            return (
                <Option
                    key={index}
                    value={value}
                    values={values}
                    onChange={onClickOption}>
                    {children}
                </Option>
            );
        });
    };

    return (
        <SelectStyle>
            {title !== false &&
                <TitleStyle>{title}</TitleStyle>
            }
            <div ref={controlRef}>
                <ContainerStyle className={className}>
                    <InputStyle
                        id={id}
                        name={name}
                        type="text"
                        ref={inputRef}
                        onClick={onClick}
                        value={toLabel()}
                        readOnly={true}
                        autoComplete="off"
                        disabled={disabled}
                        className={errorClassName}
                        $textAlign={textAlign} />
                    <SymbolStyle className={className}
                        onClick={onClick}
                        onMouseDown={event => event.preventDefault()} >
                        <Svg path={mdiChevronDown} size="20" />
                    </SymbolStyle>
                </ContainerStyle>
                {showOptions &&
                    <OptionsStyle
                        className={optionsClassName}>
                        {buildOptions(children).map(options => options)}
                    </OptionsStyle>
                }
            </div>
            <ErrorStyle
                name={errorName}
                className={typeof error === "string" ? errorClassName : ""}>
                {error}
            </ErrorStyle>
        </SelectStyle>
    );
};
